
import React from 'react';
import { Ticket } from '../../types';
import { MapPin } from 'lucide-react';

interface StoreTicketGridProps {
    tickets: Ticket[];
    onSelectStore: (siteId: string) => void;
}

export const StoreTicketGrid: React.FC<StoreTicketGridProps> = ({ tickets, onSelectStore }) => {
    // 1. Group tickets by store
    const storeMap = new Map<string, {
        siteId: string;
        siteName: string;
        count: number;
        p1Count: number;
        p2Count: number;
    }>();

    tickets.forEach(ticket => {
        if (!storeMap.has(ticket.siteId)) {
            storeMap.set(ticket.siteId, {
                siteId: ticket.siteId,
                siteName: ticket.siteName,
                count: 0,
                p1Count: 0,
                p2Count: 0
            });
        }

        const store = storeMap.get(ticket.siteId)!;
        store.count++;
        if (ticket.priority === 'P1') store.p1Count++;
        if (ticket.priority === 'P2') store.p2Count++;
    });

    const stores = Array.from(storeMap.values()).sort((a, b) => {
        // Sort by P1 count desc, then total count desc
        if (b.p1Count !== a.p1Count) return b.p1Count - a.p1Count;
        return b.count - a.count;
    });

    if (stores.length === 0) {
        return (
            <div className="text-center py-12 bg-white rounded-xl border border-dashed">
                <MapPin size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-bold text-gray-900">No hay tiendas con tickets activos</h3>
                <p className="text-gray-500">Todas las operaciones funcionan correctamente.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {stores.map(store => {
                const isCritical = store.p1Count > 0;
                const isWarning = store.p2Count > 0;

                return (
                    <div
                        key={store.siteId}
                        onClick={() => onSelectStore(store.siteId)}
                        className={`
                            bg-white p-4 rounded-xl shadow-sm border interactive-card cursor-pointer transition-all hover:shadow-md
                            ${isCritical ? 'border-l-4 border-l-red-500' : isWarning ? 'border-l-4 border-l-yellow-500' : 'border-l-4 border-l-blue-500'}
                        `}
                    >
                        <div className="flex justify-between items-start mb-3">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-600">
                                {store.siteName.substring(0, 2).toUpperCase()}
                            </div>
                            <div className={`px-2 py-1 rounded text-xs font-bold ${isCritical ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                                {store.count} {store.count === 1 ? 'Ticket' : 'Tickets'}
                            </div>
                        </div>

                        <h3 className="font-bold text-gray-900 truncate mb-1" title={store.siteName}>
                            {store.siteName}
                        </h3>

                        <div className="flex space-x-2 text-xs mt-2">
                            {store.p1Count > 0 && (
                                <span className="text-red-600 font-bold bg-red-50 px-1.5 py-0.5 rounded">
                                    {store.p1Count} Críticos
                                </span>
                            )}
                            {store.p2Count > 0 && (
                                <span className="text-yellow-600 font-bold bg-yellow-50 px-1.5 py-0.5 rounded">
                                    {store.p2Count} Altos
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
